import client from 'apollo/client';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import {
  GlobalNavCurrentUserDocument,
  useGlobalNavCurrentUserQuery
} from './GlobalNavUser.queries.generated';

interface GlobalNavUser {
  username: string;
  firstName: string;
  /** Name shown on the avatar and in the account menu. */
  fullName: string;
}

interface NameParts {
  firstName: string;
  lastName: string;
  username: string;
}

/**
 * Reproduces the `user_v2` model's client-side `getFullName()` rather than reading the
 * server's `fullName`: Ash calculates it as `first_name <> " " <> last_name`, which is NULL
 * when either half is missing, whereas the legacy accessor falls back to whichever half
 * exists — and to the username when both are absent. Legacy accounts commonly carry no
 * first/last name, so reading `fullName` straight off the API would blank their avatar.
 */
function deriveFullName({firstName, lastName, username}: NameParts): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (!firstName && !lastName) {
    return username;
  }
  return firstName || lastName;
}

/**
 * The signed-in user's identity as the global nav renders it. Held back until a session
 * exists so the query never fires on the logged-out marketing shell.
 *
 * Reads the JWT's own user (`currentUser`), never the masqueraded one — matching the
 * `sessionStore.getUserId()` this replaced, which a masquerade deliberately leaves alone.
 */
export function useGlobalNavUser(): GlobalNavUser {
  const hasSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const {data} = useGlobalNavCurrentUserQuery({skip: !hasSession});

  const user = data?.currentUser;
  const username = user?.username ?? '';
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';

  return {
    username,
    firstName,
    fullName: deriveFullName({firstName, lastName, username})
  };
}

/** Refetches the nav's user after a write Apollo can't see changes the name, such as onboarding. */
export function invalidateGlobalNavUser() {
  client
    .refetchQueries({include: [GlobalNavCurrentUserDocument]})
    .catch((error: unknown) => logger.warn('Global nav user refresh failed: %o', error));
}
